import type { Leaderboard, LeaderboardEntry } from "./board";
import {
  carriedScoreStateAt,
  scoreWriteIsStale,
  type CarriedScoreFreshness,
} from "./score-freshness";

/**
 * Board snapshots carry their rows' dates (lane fix/p3g-board-freshness, docs/handover/phase-2c.md
 * "Board snapshots carry the row date"). Each leaderboard snapshot entry holds its stored score
 * row's spot-local date and last write plus the instants its location's zone decides
 * (score-freshness.ts CarriedScoreFreshness), so every board reader judges the shared freshness
 * rule on its own clock with no zone, no extra read and no Intl call. Pure and light: the board
 * table imports it to judge again on the browser's clock.
 *
 * Snapshots written before these fields existed have none: their freshness is "unknown" and they
 * keep the write-age verdict the snapshot was written with (isStale), bounded by the snapshot's
 * own age.
 */

/** Snapshot entry fields (LeaderboardEntry). Optional: absent on snapshots written before 2 October 2026. */
export type BoardEntryFreshnessFields = {
  /** The stored score row's spot-local date (condition_scores.date). */
  scoreDate?: string;
  /** The stored score row's last write, an ISO instant (condition_scores.updated_at). */
  scoreUpdatedAt?: string;
  /** The last instant (epoch ms) the row may be shown as current; null when it never can. */
  scoreCurrentUntil?: number | null;
  /** Only for a row dated ahead of its location's local date when written: when that date begins (epoch ms). */
  scoreCurrentFrom?: number;
  /** Only for a row written after its own local day ended: the end of the nightly gap state (epoch ms). */
  scoreGapUntil?: number;
};

/**
 * How a board entry's score may be presented at one instant:
 * - "current": today's score at the location (the spot pages' "current");
 * - "updating": the nightly gap: the row is dated the location's previous local day and today's
 *   run has not written yet (the spot pages show the previous run's forecast or "Score
 *   unavailable"); never ranked or promoted as today's score;
 * - "stale": neither (no refresh within 5 hours, or older);
 * - "unknown": the snapshot predates the fields; `isStale` is the write-age verdict it was written with.
 */
export type BoardScoreFreshness = "current" | "updating" | "stale" | "unknown";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** The snapshot fields for a carried row; {} when the row's stamp was unreadable (the entry is then "unknown"). */
export function boardEntryFreshnessFields(carried: CarriedScoreFreshness | null): BoardEntryFreshnessFields {
  if (!carried) return {};
  return {
    scoreDate: carried.date,
    scoreUpdatedAt: carried.updatedAt,
    scoreCurrentUntil: carried.currentUntilMs,
    ...(carried.currentFromMs !== undefined ? { scoreCurrentFrom: carried.currentFromMs } : {}),
    ...(carried.gapUntilMs !== undefined ? { scoreGapUntil: carried.gapUntilMs } : {}),
  };
}

/** The carried form read back from an entry, or null for an entry without readable fields (an older snapshot). */
export function boardEntryCarriedFreshness(entry: BoardEntryFreshnessFields): CarriedScoreFreshness | null {
  const { scoreDate, scoreUpdatedAt, scoreCurrentUntil } = entry;
  if (typeof scoreDate !== "string" || !ISO_DATE.test(scoreDate)) return null;
  if (typeof scoreUpdatedAt !== "string" || Number.isNaN(Date.parse(scoreUpdatedAt))) return null;
  if (scoreCurrentUntil !== null && finiteOrUndefined(scoreCurrentUntil) === undefined) return null;
  const currentFromMs = finiteOrUndefined(entry.scoreCurrentFrom);
  const gapUntilMs = finiteOrUndefined(entry.scoreGapUntil);
  return {
    date: scoreDate,
    updatedAt: scoreUpdatedAt,
    currentUntilMs: scoreCurrentUntil ?? null,
    ...(currentFromMs !== undefined ? { currentFromMs } : {}),
    ...(gapUntilMs !== undefined ? { gapUntilMs } : {}),
  };
}

const FRESHNESS_KEYS = ["scoreDate", "scoreUpdatedAt", "scoreCurrentUntil", "scoreCurrentFrom", "scoreGapUntil"] as const;

/** Only the carried freshness fields of an entry, pin or sport ({} when it has none): a reading's stamp. */
export function freshnessFieldsOf(source: BoardEntryFreshnessFields | null | undefined): BoardEntryFreshnessFields {
  const out: Record<string, unknown> = {};
  if (!source) return out;
  for (const key of FRESHNESS_KEYS) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out as BoardEntryFreshnessFields;
}

/**
 * Whether a reading the server presented as current may still be presented so at `nowMs` (a
 * client judging again on the browser's clock): its row is still current, or it carries no
 * readable stamp (an older snapshot), whose server verdict stands. Only ever withdraws.
 */
export function boardEntryStillCurrentAt(entry: BoardEntryFreshnessFields | null | undefined, nowMs: number): boolean {
  if (!entry) return true;
  const freshness = boardEntryFreshnessAt(entry, nowMs);
  return freshness === "current" || freshness === "unknown";
}

/** The shared rule on an entry's carried fields at one instant. */
export function boardEntryFreshnessAt(entry: BoardEntryFreshnessFields, nowMs: number): BoardScoreFreshness {
  const carried = boardEntryCarriedFreshness(entry);
  if (!carried) return "unknown";
  const state = carriedScoreStateAt(carried, nowMs);
  return state.kind === "current" ? "current" : state.kind === "forecast" ? "updating" : "stale";
}

/**
 * The next instant after `nowMs` at which this row's verdict can change what a reader shows: a
 * current row stops being current (the millisecond after scoreCurrentUntil), or a row dated ahead
 * of its zone's day becomes current (scoreCurrentFrom). Null when neither lies ahead: an
 * updating, stale or "unknown" row stays unpromoted. For bounding how long a judged response may
 * be cached (a CDN's s-maxage).
 */
export function boardEntryNextChangeMs(entry: BoardEntryFreshnessFields, nowMs: number): number | null {
  const carried = boardEntryCarriedFreshness(entry);
  if (!carried) return null;
  if (carried.currentFromMs !== undefined && carried.currentFromMs > nowMs) return carried.currentFromMs;
  if (carriedScoreStateAt(carried, nowMs).kind !== "current" || carried.currentUntilMs === null) return null;
  return carried.currentUntilMs + 1;
}

/** The earliest of several next-change instants (boardEntryNextChangeMs); null when none. */
export function earliestChangeMs(instants: Iterable<number | null>): number | null {
  let earliest: number | null = null;
  for (const instant of instants) {
    if (instant !== null && Number.isFinite(instant) && (earliest === null || instant < earliest)) earliest = instant;
  }
  return earliest;
}

/** Seconds a CDN may serve a judged response stale while it refetches: a minute, as the browser's minute clock. */
export const JUDGED_RESPONSE_STALE_WHILE_REVALIDATE_SECONDS = 60;

/**
 * A public Cache-Control for a response judged at `nowMs`: kept until the next change of any
 * verdict in it (boardEntryNextChangeMs), at most `maxSeconds`, at least a second, then served
 * stale for at most a minute while the CDN refetches. So a body judged before a location's local
 * midnight is never served as current for more than a minute after it.
 */
export function judgedPublicCacheControl(nextChangeMs: number | null, nowMs: number, maxSeconds: number): string {
  const untilChange = nextChangeMs === null ? maxSeconds : Math.floor((nextChangeMs - nowMs) / 1000);
  const sMaxAge = Math.max(1, Math.min(maxSeconds, untilChange));
  return `public, s-maxage=${sMaxAge}, stale-while-revalidate=${JUDGED_RESPONSE_STALE_WHILE_REVALIDATE_SECONDS}`;
}

type JudgedEntry = Pick<LeaderboardEntry, "isStale" | "rank"> & Partial<Pick<LeaderboardEntry, "seasonClosed">> & BoardEntryFreshnessFields;

/**
 * An entry as of one instant: one whose row is no longer the location's current score (stale, or
 * the nightly gap) is marked stale with no rank, which every board surface already renders as
 * "Updating" and leaves out of its ranking, podium and promotions. Closed for the season is no
 * claim about today's score, so in the nightly gap such an entry only loses its rank and stays
 * with the closed ones (it would otherwise move to "Updating" every night of the off season); a
 * stale one is marked stale as any other. An entry the snapshot already marked stale stays stale
 * (time only moves forward), and an "unknown" entry keeps its verdict. Returns the same object
 * when nothing changes.
 */
export function boardEntryAt<T extends JudgedEntry>(entry: T, nowMs: number): T {
  if (entry.isStale && entry.rank === 0) return entry;
  const freshness = boardEntryFreshnessAt(entry, nowMs);
  if (freshness === "unknown" || freshness === "current") return entry;
  if (freshness === "updating" && entry.seasonClosed === true) return entry.rank === 0 ? entry : { ...entry, rank: 0 };
  return { ...entry, isStale: true, rank: 0 };
}

/** boardEntryAt over a list; the same array when no entry changes. */
export function boardEntriesAt<T extends JudgedEntry>(entries: readonly T[], nowMs: number): readonly T[] {
  let changed = false;
  const judged = entries.map((entry) => {
    const next = boardEntryAt(entry, nowMs);
    if (next !== entry) changed = true;
    return next;
  });
  return changed ? judged : entries;
}

/**
 * A board as of the request: every entry judged on its own row (boardEntryAt) and, for entries
 * from older snapshots, the snapshot's own write age (a snapshot not written for 5 hours holds no
 * current entry; for entries that carry their row this is implied, since the snapshot is written
 * after its rows). Pure, so a board cached for 3 hours is reused without presenting an earlier
 * local day's row as today's.
 */
export function leaderboardAtRequest(board: Leaderboard, nowMs: number): Leaderboard {
  if (board.entries.length === 0) return board;
  const snapshotStale = scoreWriteIsStale(board.lastUpdated, nowMs);
  let changed = false;
  const entries = board.entries.map((entry) => {
    const next = snapshotStale
      ? entry.isStale && entry.rank === 0 ? entry : { ...entry, isStale: true, rank: 0 }
      : boardEntryAt(entry, nowMs);
    if (next !== entry) changed = true;
    return next;
  });
  return changed ? { ...board, entries } : board;
}
